import { ReactNode, useEffect, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import {
  formatDateToPtBr,
  initialQueryState,
  OptionsHelper,
  SituacaoInterconsulta,
  useDebounce,
  validateDateRange,
} from '../../../../../../../_metronic/helpers'
import { useQueryRequest } from '../../core/QueryRequestProvider'
import { useQueryResponse } from '../../core/QueryResponseProvider'
import { useListFilters } from '../../core/ListFiltersProvider'
import {
  ActiveFilter,
  ActiveFiltersBar,
  CustomSelect,
  DateInput,
  Input,
  IOption,
  TableFilter,
  TableFilters,
} from '../../../../../../components'
import { useT } from '_metronic/i18n/Metronici18n'

type Props = {
  /** Destaca o período como obrigatório (ex.: tentou exportar sem período). */
  forceDateRangeError?: boolean
  /** Conteúdo à direita da linha dos campos (ex.: "Solicitar parecer"). */
  actions?: ReactNode
}

export const PareceresListSearchComponent = ({ forceDateRangeError, actions }: Props) => {
  const { updateState } = useQueryRequest()
  const { response, isLoading } = useQueryResponse()
  const { filters, setFilter, clearFilters } = useListFilters()
  const { protocolo } = useParams<{ protocolo?: string }>()
  const { t } = useT()
  const total = response?.totalElements ?? 0

  // Rota /pareceres/:protocolo já abre filtrada pelo protocolo.
  useEffect(() => {
    if (protocolo) setFilter('protocolo', protocolo)
  }, [protocolo, setFilter])

  // Período: no máximo 31 dias; os limites dos inputs acompanham a outra data.
  const { error: dateError, limits: dateLimits } = useMemo(
    () => validateDateRange(filters.dataInicial, filters.dataFinal, { maxRangeDays: 31 }),
    [filters.dataInicial, filters.dataFinal]
  )

  const debouncedFilters = useDebounce(filters, 500)

  // A rota do solicitante já restringe a interconsultas criadas pelo usuário logado. Período
  // incompleto ou inválido fica fora da busca; os demais filtros continuam valendo.
  const search = useMemo(() => {
    const { range, dataInicial, dataFinal } = debouncedFilters
    const { error } = validateDateRange(dataInicial, dataFinal, { maxRangeDays: 31 })
    const situacoes = debouncedFilters.situacoes.map((s) => s.value).join('|')

    return [
      situacoes ? `situacaoInterconsulta:${situacoes}` : '',
      debouncedFilters.protocolo ? `protocolo~*${debouncedFilters.protocolo.trim()}*` : '',
      debouncedFilters.paciente ? `paciente.pessoa.nome~*${debouncedFilters.paciente.trim()}*` : '',
      debouncedFilters.empresa ? `paciente.empresa:${debouncedFilters.empresa.value}` : '',
      !error && range && dataInicial && dataFinal
        ? `${range.value}>${dataInicial},${range.value}<${dataFinal}`
        : '',
    ]
      .filter(Boolean)
      .join(',')
  }, [debouncedFilters])

  useEffect(() => {
    updateState({ ...initialQueryState, search })
  }, [search, updateState])

  // Período começado e incompleto (ou exportação sem período): destaca o que falta.
  const periodoIniciado = !!(
    filters.range ||
    filters.dataInicial ||
    filters.dataFinal ||
    forceDateRangeError
  )
  const periodoCompleto = !!(filters.range && filters.dataInicial && filters.dataFinal)
  const faltando = (campo: unknown) => periodoIniciado && !periodoCompleto && !campo

  // Um erro por data: o que falta preencher tem prioridade sobre o intervalo inválido.
  const erroInicio = faltando(filters.dataInicial)
    ? 'Selecione uma data inicial'
    : dateError.includes('inicial')
    ? dateError
    : undefined
  const erroFim = faltando(filters.dataFinal)
    ? 'Selecione uma data final'
    : dateError && !dateError.includes('inicial')
    ? dateError
    : undefined

  const limparPeriodo = () => {
    setFilter('range', undefined)
    setFilter('dataInicial', '')
    setFilter('dataFinal', '')
  }

  // Chips da faixa "Filtros ativos": cada × limpa só o seu campo (o período sai inteiro).
  // Data pura via split (formatDateToPtBr), sem `new Date`, para não voltar um dia no fuso do Brasil.
  const activeFilters: ActiveFilter[] = [
    filters.situacoes.length > 0 && {
      key: 'situacoes',
      label: 'Situação',
      value: filters.situacoes.map((s) => s.label).join(' ou '),
      onRemove: () => setFilter('situacoes', []),
    },
    filters.paciente.trim() && {
      key: 'paciente',
      label: t('cols.paciente'),
      value: `“${filters.paciente.trim()}”`,
      onRemove: () => setFilter('paciente', ''),
    },
    periodoCompleto && {
      key: 'periodo',
      label: filters.range!.label,
      value: `${formatDateToPtBr(filters.dataInicial)} a ${formatDateToPtBr(filters.dataFinal)}`,
      onRemove: limparPeriodo,
    },
    filters.protocolo.trim() && {
      key: 'protocolo',
      label: t('cols.protocolo'),
      value: `“${filters.protocolo.trim()}”`,
      onRemove: () => setFilter('protocolo', ''),
    },
    filters.empresa && {
      key: 'empresa',
      label: t('empresa.label'),
      value: filters.empresa.label,
      onRemove: () => setFilter('empresa', undefined),
    },
  ].filter((filter): filter is ActiveFilter => Boolean(filter))

  const moreFiltersActiveCount = [
    filters.situacoes.length > 0,
    filters.protocolo.trim(),
    filters.empresa,
  ].filter(Boolean).length

  return (
    <TableFilters
      layout='stacked'
      actions={actions}
      moreFiltersActiveCount={moreFiltersActiveCount}
      moreFilters={
        <>
          <TableFilter label='Situação' htmlFor='pareceres-situacao' wide>
            <CustomSelect
              inputId='pareceres-situacao'
              isMulti
              hideSelectAll
              entity='situacao'
              onChange={(situacoes?: IOption<SituacaoInterconsulta>[]) =>
                setFilter('situacoes', situacoes ?? [])
              }
              fetchOptions={OptionsHelper.getOptionsSituacoesInterconsulta}
              value={filters.situacoes}
            />
          </TableFilter>
          <TableFilter label={t('cols.protocolo')}>
            <Input
              entity='protocolo'
              value={filters.protocolo}
              onChange={(e) => setFilter('protocolo', e.target.value)}
            />
          </TableFilter>
          <TableFilter label={t('empresa.label')}>
            <CustomSelect
              entity='empresa'
              onChange={(empresa?: IOption<number>) => setFilter('empresa', empresa ?? undefined)}
              fetchOptions={OptionsHelper.getOptionsEmpresas}
              value={filters.empresa}
            />
          </TableFilter>
        </>
      }
      footer={
        <ActiveFiltersBar
          filters={activeFilters}
          onClearAll={clearFilters}
          summary={
            isLoading
              ? 'Buscando…'
              : `${total} ${total === 1 ? 'parecer encontrado' : 'pareceres encontrados'}`
          }
        />
      }
    >
      <TableFilter label={t('cols.paciente')}>
        <Input
          entity='paciente'
          value={filters.paciente}
          onChange={(e) => setFilter('paciente', e.target.value)}
        />
      </TableFilter>
      <TableFilter
        label='Consultas entre'
        error={faltando(filters.range) ? 'Selecione uma opção' : undefined}
      >
        <CustomSelect
          placeholder='Selecione uma opção'
          options={OptionsHelper.consultaDatasOptions}
          onChange={(range?: IOption<string>) => setFilter('range', range ?? undefined)}
          value={filters.range}
        />
      </TableFilter>
      <TableFilter label='Data inicial' error={erroInicio}>
        <DateInput
          value={filters.dataInicial}
          onChange={(e) => setFilter('dataInicial', e.target.value)}
          minDate={dateLimits.startMin}
          maxDate={dateLimits.startMax}
          isInvalid={Boolean(erroInicio)}
        />
      </TableFilter>
      <TableFilter label='Data final' error={erroFim}>
        <DateInput
          value={filters.dataFinal}
          onChange={(e) => setFilter('dataFinal', e.target.value)}
          minDate={dateLimits.endMin}
          maxDate={dateLimits.endMax}
          isInvalid={Boolean(erroFim)}
        />
      </TableFilter>
    </TableFilters>
  )
}
